import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import { Badge, Button } from "reactstrap";
import { useIntl } from "react-intl";
import { User } from "../../../../cores/session/interfaces/models/user";
import {
  SortDirection,
  UserSortField,
} from "../../../../cores/user/interfaces/api/get-users";
import { FreeTrialBadge } from "../../companies/FreeTrialBadge";
import { PlanBadge } from "../../companies/PlanBadge";
import { StatusBadge } from "../../companies/StatusBadge";
import "../../../styles/UsersTable.scss";

interface Props {
  users: User[];
  onEditUser: (user: User) => void;
  selectedIds: string[];
  onToggleUser: (userId: string) => void;
  onToggleAllUsers: () => void;
  sortBy: UserSortField;
  sortDirection: SortDirection;
  onSort: (field: UserSortField) => void;
}

export default function UsersTable({
  users,
  onEditUser,
  selectedIds,
  onToggleUser,
  onToggleAllUsers,
  sortBy,
  sortDirection,
  onSort,
}: Props) {
  const intl = useIntl();
  const allSelected = users.length > 0 && users.every(({ id }) => selectedIds.includes(id));
  const formatDateTime = (value?: string) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return intl.formatDate(date, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const roleLabels = useMemo(
    () => ({
      admin: intl.formatMessage({ id: "app.adminUsers.admin?" }),
      reseller: intl.formatMessage({ id: "app.sidebar.isReseller" }),
      user: intl.formatMessage({ id: "app.adminUsers.user?" }),
    }),
    [intl]
  );

  const sortableHeader = (
    field: UserSortField,
    label: React.ReactNode,
    className = ""
  ) => {
    const active = sortBy === field;
    return (
      <th
        className={`usersTable__th ${className}`.trim()}
        aria-sort={
          active
            ? sortDirection === "ASC"
              ? "ascending"
              : "descending"
            : "none"
        }
      >
        <button
          type="button"
          className="usersTable__sortButton"
          onClick={() => onSort(field)}
        >
          <span>{label}</span>
          <span className="usersTable__sortIndicator" aria-hidden="true">
            {active ? (sortDirection === "ASC" ? "▲" : "▼") : "↕"}
          </span>
        </button>
      </th>
    );
  };

  return (
    <div className="usersTableWrapper">
      <div className="usersTable__mobileToolbar">
        <label className="usersTable__mobileSelectAll">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleAllUsers}
          />
          <span>
            {intl.formatMessage({
              id: "app.users.bulk.selectAll",
              defaultMessage: "Seleccionar todos los usuarios visibles",
            })}
          </span>
        </label>
        <div className="usersTable__mobileSort">
          <label htmlFor="users-mobile-sort">
            {intl.formatMessage({ id: "app.users.sortBy" })}
          </label>
          <div>
            <select
              id="users-mobile-sort"
              value={sortBy}
              onChange={(event) => onSort(event.target.value as UserSortField)}
            >
              <option value="id">ID</option>
              <option value="lastName">
                {intl.formatMessage({ id: "app.contacts.name" })}
              </option>
              <option value="email">
                {intl.formatMessage({ id: "app.contacts.email" })}
              </option>
              <option value="phoneNumber">
                {intl.formatMessage({ id: "app.contacts.phone" })}
              </option>
              <option value="createdAt">
                {intl.formatMessage({ id: "app.users.createdAt" })}
              </option>
              <option value="lastLoginAt">
                {intl.formatMessage({ id: "app.users.lastLoginAt" })}
              </option>
              <option value="status">
                {intl.formatMessage({ id: "app.crmBusiness.status" })}
              </option>
            </select>
            <button
              type="button"
              onClick={() => onSort(sortBy)}
              aria-label={intl.formatMessage({
                id: "app.users.changeSortDirection",
              })}
            >
              {sortDirection === "ASC" ? "▲" : "▼"}
            </button>
          </div>
        </div>
      </div>
      <table className="usersTable">
        <thead>
          <tr>
            <th className="usersTable__th usersTable__th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAllUsers}
                aria-label={intl.formatMessage({
                  id: "app.users.bulk.selectAll",
                  defaultMessage: "Seleccionar todos los usuarios visibles",
                })}
              />
            </th>
            {sortableHeader("id", "ID", "usersTable__th--narrow")}
            {sortableHeader(
              "lastName",
              intl.formatMessage({ id: "app.contacts.name" }),
              "usersTable__th--name"
            )}
            {sortableHeader(
              "email",
              intl.formatMessage({ id: "app.contacts.email" })
            )}
            {sortableHeader(
              "phoneNumber",
              intl.formatMessage({ id: "app.contacts.phone" })
            )}
            {sortableHeader(
              "createdAt",
              intl.formatMessage({ id: "app.users.createdAt" }),
              "usersTable__th--date"
            )}
            {sortableHeader(
              "lastLoginAt",
              intl.formatMessage({ id: "app.users.lastLoginAt" }),
              "usersTable__th--date"
            )}
            <th className="usersTable__th">
              {intl.formatMessage({ id: "app.users.tableRole" })}
            </th>
            {sortableHeader(
              "status",
              intl.formatMessage({ id: "app.crmBusiness.status" })
            )}
            <th className="usersTable__th">
              {intl.formatMessage({ id: "app.users.tablePlan" })}
            </th>
            <th className="usersTable__th usersTable__th--actions">
              {intl.formatMessage({ id: "app.table.actions", defaultMessage: "Acciones" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr
              key={user.id}
              className="usersTable__tr"
              data-selected={selectedIds.includes(user.id) ? "true" : "false"}
            >
              <td
                className="usersTable__td usersTable__td--checkbox"
                data-label={intl.formatMessage({
                  id: "app.users.bulk.selectOneShort",
                  defaultMessage: "Seleccionar",
                })}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => onToggleUser(user.id)}
                  aria-label={intl.formatMessage(
                    {
                      id: "app.users.bulk.selectOne",
                      defaultMessage: "Seleccionar usuario {name}",
                    },
                    { name: `${user.lastName} ${user.firstName}`.trim() || user.email }
                  )}
                />
              </td>
              <td className="usersTable__td usersTable__td--muted" data-label="ID">
                #{user.id}
              </td>
              <td
                className="usersTable__td usersTable__td--name"
                data-label={intl.formatMessage({ id: "app.contacts.name" })}
              >
                <div className="usersTable__nameCell">
                  <FontAwesomeIcon icon={["far", "id-card"]} className="usersTable__nameIcon" />
                  <span>{`${user.lastName} ${user.firstName}`}</span>
                </div>
              </td>
              <td
                className="usersTable__td usersTable__td--ellipsis"
                data-label={intl.formatMessage({ id: "app.contacts.email" })}
              >
                {user.email}
              </td>
              <td
                className="usersTable__td"
                data-label={intl.formatMessage({ id: "app.contacts.phone" })}
              >
                {user.phoneNumber || "—"}
              </td>
              <td
                className="usersTable__td usersTable__td--date"
                data-label={intl.formatMessage({ id: "app.users.createdAt" })}
              >
                {formatDateTime(user.createdAt)}
              </td>
              <td
                className="usersTable__td usersTable__td--date"
                data-label={intl.formatMessage({ id: "app.users.lastLoginAt" })}
              >
                {user.lastLoginAt
                  ? formatDateTime(user.lastLoginAt)
                  : intl.formatMessage({ id: "app.users.noLoginRecord" })}
              </td>
              <td
                className="usersTable__td"
                data-label={intl.formatMessage({ id: "app.users.tableRole" })}
              >
                {user.userRole ? (
                  <div className="usersTable__badges">
                    {(Object.entries(user.userRole) as [string, boolean][]).map(
                      ([role, active]) =>
                        active ? (
                          <Badge pill className="usersTable__roleBadge" key={role}>
                            <span className="text-uppercase small">
                              {(roleLabels as Record<string, string>)[role] ?? role}
                            </span>
                          </Badge>
                        ) : null
                    )}
                  </div>
                ) : (
                  "—"
                )}
              </td>
              <td
                className="usersTable__td"
                data-label={intl.formatMessage({ id: "app.crmBusiness.status" })}
              >
                <StatusBadge status={user.status} show />
              </td>
              <td
                className="usersTable__td"
                data-label={intl.formatMessage({ id: "app.users.tablePlan" })}
              >
                <div className="usersTable__planCell">
                  {user.freeTrial && <FreeTrialBadge freeTrial={user.freeTrial} />}
                  {user.userPlan ? (
                    <PlanBadge plan={user.userPlan} show />
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </div>
              </td>
              <td
                className="usersTable__td usersTable__td--actions"
                data-label={intl.formatMessage({
                  id: "app.table.actions",
                  defaultMessage: "Acciones",
                })}
              >
                <Button
                  color="secondary"
                  size="sm"
                  className="usersTable__btn"
                  onClick={() => onEditUser(user)}
                >
                  {intl.formatMessage({ id: "app.adminCompanies.edit" })}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
